

import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import { MentionsInput, Mention } from 'react-mentions';
import styled, { css } from 'styled-components';
import Api from '../utils/Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAsc, faSortDesc } from '@fortawesome/free-solid-svg-icons';
import UserCard from './UserCard';

const fetchComments = async ({ parent, parentModel, context, contextId }) => {
  const [error, comments] = await Api.getComments({ parent, parentModel, context, contextId });
  if (!error) return comments;
  toast.error(Api.error(error));
  return [];
}

const sort = (comments, dir) => {
  const arr = [...comments];
  return arr.sort((a, b) => {
    if (a.createdOn > b.createdOn) return dir;
    else if (a.createdOn > b.createdOn) return -dir;
    else return 0;
  });
}

const styles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;

  > .comment {
    border: 1px solid gray;
    border-radius: 5px;
    padding: 5px;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 3px; 
    > .mentions-input {
        font-size: 1em;
        padding: 10px;
        padding-bottom: 5px;
        display: flex;
        align-items: center;
      & .mentions-input__control {
        background-color: #fff;
        font-size: 1em;
        font-weight: normal;
      }
      & .mentions-input__input {
        height: 100%;
      }
      & .mentions-input__suggestions {
        padding: 5px;
        border: 1px solid gray;

        & .mentions-input__suggestions__list {
          display: flex;
          flex-direction: column;
          gap: 5px;

          .user-card {
            &:hover {
              font-weight: bold;
            }
          }
        }
      }
    }
    > .meta {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      font-size: 0.75em;
      font-style: italic;
      > .user {
        font-weight: bold;
      }
      > .time {
        font-weight: normal;
      }
    }
    > .text {}
    &.edit {
      display: flex;
      flex-direction: column;
      gap: 5px;
      border: none;
      padding: 0px;
      justify-items: flex-start;
      > .buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        align-items: center;
        justify-items: flex-start;
        > .send {
          font-size: 0.75em;
          align-self: flex-start;
        }
        > .sort {
          color: white;
          background-color:#057bff;
          border-radius: 3px;
          border: 1px solid #057bff;
          padding: 5px;
          padding-left: 10px;
          padding-right: 10px;
        }
      }

    }
  }
`;

const Comments = styled(({ className, parent, parentModel, users, context: { model: context, id: contextId }, onUpdate }) => {

  const { data: comments = [], refetch } =
    useQuery(
      ['comments', parent, parentModel, context, contextId],
      () => fetchComments({ parent, parentModel, context, contextId })
    );
  const [sortDir, setSortDir] = useState(1);
  const sorted = useMemo(() => sort(comments, sortDir), [comments, sortDir]);
  const [comment, setComment] = useState('');
  const displayUsers = users.map(user => ({ ...user, display: user.firstname}));

  const handleSendComment = async () => {
    // TODO need a better match for mentions.
    // This breaks if a close paren show up later in the input;
    const mentions  = comment.match(/[^(]+(?=\))/g);
    const [error] = await Api.postComment({ parent, parentModel, context, contextId, text: comment, mentions });
    if (error) {
      toast.error(Api.getError(error));
      console.error(error);
      return;
    } else {
      setComment('');
      const { data: newComments = [] } = await refetch();
      onUpdate(newComments.length);
    }
  }

  const onChange = e => {
    const text = e.target.value;
    setComment(text);
  }

  return (
    <div className={className}>
      <div className='comment edit'>
        <MentionsInput
          className='mentions-input'
          forceSuggestionsAboveCursor = {true}
          value={comment}
          onChange={onChange}>
          <Mention
            trigger='@'
            data={displayUsers}
            renderSuggestion={user => <UserCard className = 'user-card' user={user} />}
          />
        </MentionsInput>
        {/* <textarea value={comment} placeholder='Add your comment here' onChange={e => handleTextChange(e.target.value)} /> */}
        <div className='buttons'>
          <button disabled={!comment} className='send primary' onClick={() => handleSendComment()}>Send</button>
          <FontAwesomeIcon className='sort' icon={sortDir > 0 ? faSortAsc : faSortDesc} onClick={() => setSortDir(prev => prev * -1)} />
        </div>
      </div>
      {sorted.map((comment, i) => (
        <div key={i} className='comment'>
          <div className='meta'>
            <div className='user'>{comment.user.email}</div>
            <div className='time'>{moment(comment.createdOn).fromNow()}</div>
          </div>
          <div className='text'>
            <ReactMarkdown>{comment.text}</ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  )
})`${styles}`;

export default Comments;
